import { useMemo, useState } from 'react';
import { Tabs } from '@/components/common/Tabs';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { usePermission } from '@/permissions/usePermission';
import { SettingManagementPermissions } from './permissions';
import { TimeZoneTab } from './TimeZoneTab';
import { EmailingTab } from './EmailingTab';

type TabKey = 'emailing' | 'timeZone';

// Mirrors the ABP /SettingManagement page: a tab per granted settings group.
// The tenant admin is granted Emailing (+ .Test) and TimeZone.
export function SettingManagementPage() {
  const can = usePermission();
  const canEmailing = can(SettingManagementPermissions.emailing);
  const canEmailingTest = can(SettingManagementPermissions.emailingTest);
  const canTimeZone = can(SettingManagementPermissions.timeZone);

  const tabs = useMemo(() => {
    const list: { key: TabKey; label: string }[] = [];
    if (canEmailing) list.push({ key: 'emailing', label: 'Email settings' });
    if (canTimeZone) list.push({ key: 'timeZone', label: 'Time zone' });
    return list;
  }, [canEmailing, canTimeZone]);

  const [active, setActive] = useState<TabKey | null>(tabs[0]?.key ?? null);

  return (
    <div>
      <PageHeader title="Settings" breadcrumb={['Home', 'Settings']} />

      <div className="card">
        {tabs.length === 0 ? (
          <div className="card-body">
            <EmptyState message="You do not have permission to view any settings." />
          </div>
        ) : (
          <>
            {/* Tabs live in the card header, like the other tabbed pages. */}
            <div className="card-header p-0">
              <Tabs
                tabs={tabs.map((t) => ({ key: t.key, title: t.label }))}
                active={active ?? ''}
                onChange={(k) => setActive(k as TabKey)}
                variant="underline"
              />
            </div>
            <div className="card-body">
              <div className="tab-content">
                {active === 'emailing' && canEmailing && (
                  <div className="tab-pane active" role="tabpanel">
                    <EmailingTab canChange={canEmailing} canSendTest={canEmailingTest} />
                  </div>
                )}
                {active === 'timeZone' && canTimeZone && (
                  <div className="tab-pane active" role="tabpanel">
                    <TimeZoneTab canChange={canTimeZone} />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
